import { useQuery } from "@tanstack/react-query";
import { parseISO } from "date-fns/parseISO";

import {
  ExporterMetricsEndpoint,
  JobsSummaryBE,
  JobsSummaryResponse,
} from "src/api/endpoints";
import {
  MetricsDecisionsResponse,
  MetricsErrorsResponse,
  MetricsLatencyResponse,
  MetricsNodeStatusCountsResponse,
  MetricsSummaryResponse,
  SummaryBE,
} from "src/api/types";
import { useIsIcebergMetricsEnabled } from "src/hooks/useIsIcebergMetricsEnabled";
import { computeRunningTotal } from "src/performance/nodeStatusUtils";
import {
  ErrorsFilters,
  JobsSummary,
  JobsSummaryResult,
  LatencyFunction,
  PerformanceFilters,
  Summary,
  SummaryResult,
} from "src/performance/types";
import {
  getIntervalForTimeWindow,
  intervalToSeconds,
} from "src/performance/utils";
import { StrictDateRange } from "src/utils/timeWindow";

const mapSummary = (summary: SummaryBE): Summary => ({
  decisions: summary.decisions,
  errors: summary.errors,
  errorRate: summary.decisions > 0 ? summary.errors / summary.decisions : 0,
  latencyP90: summary.latency_p90,
});

const transformSummaryResponse = (
  data: MetricsSummaryResponse,
): SummaryResult => ({
  currentPeriod: mapSummary(data.current_period),
  previousPeriod: mapSummary(data.previous_period),
});

const getISODatesFromTimeWindow = (
  timeWindow: StrictDateRange,
): { startDate: string; endDate: string } => {
  const startDate = timeWindow.from.toISOString();
  const endDate = timeWindow.to.toISOString();

  return { startDate, endDate };
};

const INITIAL_SUMMARY = {
  current_period: { decisions: 0, errors: 0, latency_p90: 0 },
  previous_period: { decisions: 0, errors: 0, latency_p90: 0 },
};
const INITIAL_JOBS_SUMMARY = {
  current_period: { decisions: 0, errors: 0 },
  previous_period: { decisions: 0, errors: 0 },
};
const ONE_MINUTE_IN_MS = 1000 * 60;

export const useAvailableVersions = (
  baseUrl: Nullable<string> = null,
  { versions = [], timeWindow, flowId }: PerformanceFilters,
) => {
  const { startDate, endDate } = getISODatesFromTimeWindow(timeWindow);
  return useQuery({
    queryKey: [
      "metrics",
      "available_versions",
      versions.join(","),
      startDate,
      endDate,
      flowId,
    ],
    queryFn: () =>
      ExporterMetricsEndpoint.postAvailableVersionsSearch(baseUrl!, {
        versions,
        startDate,
        endDate,
        flowId,
      }),
    enabled: Boolean(baseUrl) && versions.length > 0,
    staleTime: ONE_MINUTE_IN_MS,
  });
};

export const useSummary = (
  baseUrl: Nullable<string> = null,
  { versions, timeWindow, flowId }: PerformanceFilters,
  options: { enabled?: boolean } = {},
) => {
  const { startDate, endDate } = getISODatesFromTimeWindow(timeWindow);
  const isIcebergMetricsEnabled = useIsIcebergMetricsEnabled();

  const enabled =
    Boolean(baseUrl) &&
    (versions === undefined || versions.length > 0) &&
    (options.enabled ?? true);

  return useQuery({
    queryKey: [
      "metrics",
      "summary",
      baseUrl,
      startDate,
      endDate,
      flowId,
      isIcebergMetricsEnabled,
      ...(versions ?? ["all_versions"]),
    ],
    queryFn: () =>
      ExporterMetricsEndpoint.getSummary(
        baseUrl!,
        {
          versions,
          startDate,
          endDate,
          flowId,
        },
        isIcebergMetricsEnabled,
      ),
    enabled,
    staleTime: ONE_MINUTE_IN_MS,
    select: transformSummaryResponse,
    initialData: INITIAL_SUMMARY,
    initialDataUpdatedAt: 0,
    // Disable to prevent race condition when rapidly switching version filters
    experimental_prefetchInRender: false,
  });
};

const mapJobsSummary = (summary: JobsSummaryBE): JobsSummary => ({
  decisions: summary.decisions,
  errors: summary.errors,
  errorRate: summary.decisions > 0 ? summary.errors / summary.decisions : 0,
});
const transformJobsSummaryResponse = (
  data: JobsSummaryResponse,
): JobsSummaryResult => ({
  currentPeriod: mapJobsSummary(data.current_period),
  previousPeriod: mapJobsSummary(data.previous_period),
});

export const useJobsSummary = (
  baseUrl: Nullable<string> = null,
  {
    flowId,
    timeWindow,
    options,
  }: {
    timeWindow: StrictDateRange;
    flowId: string;
    options: { enabled: boolean };
  },
) => {
  const { startDate, endDate } = getISODatesFromTimeWindow(timeWindow);

  return useQuery({
    queryKey: ["metrics", "jobs_summary", baseUrl, startDate, endDate, flowId],
    queryFn: () =>
      ExporterMetricsEndpoint.getJobsSummary(baseUrl!, {
        startDate,
        endDate,
        flowId,
      }),
    select: transformJobsSummaryResponse,
    initialData: INITIAL_JOBS_SUMMARY,
    enabled: options.enabled,
  });
};

type BinData = { bin: string };

const parseBinDate = <T extends BinData>(
  data: T[],
): (Omit<T, "bin"> & { bin: Date })[] =>
  data.map((d) => ({ ...d, bin: parseISO(d.bin) }));

const parseDecisions = (data: MetricsDecisionsResponse) =>
  parseBinDate(data.by_version);

export const useDecisions = (
  baseUrl: Nullable<string> = null,
  { versions, timeWindow, flowId }: PerformanceFilters,
  options: { enabled?: boolean } = {},
) => {
  const { startDate, endDate } = getISODatesFromTimeWindow(timeWindow);
  const interval = getIntervalForTimeWindow(timeWindow);
  const isIcebergMetricsEnabled = useIsIcebergMetricsEnabled();

  const enabled =
    Boolean(baseUrl) &&
    (versions === undefined || versions.length > 0) &&
    (options.enabled ?? true);

  return useQuery({
    queryKey: [
      "metrics",
      "decisions",
      startDate,
      endDate,
      flowId,
      isIcebergMetricsEnabled,
      ...(versions ?? ["all_versions"]),
    ],
    queryFn: () =>
      ExporterMetricsEndpoint.getDecisions(
        baseUrl!,
        {
          versions,
          startDate,
          endDate,
          interval: intervalToSeconds(interval),
          flowId,
        },
        isIcebergMetricsEnabled,
      ),
    enabled,
    staleTime: ONE_MINUTE_IN_MS,
    select: parseDecisions,
    // Disable to prevent race condition when rapidly switching version filters
    experimental_prefetchInRender: false,
  });
};

const parseErrors = (data: MetricsErrorsResponse) => ({
  by_error: parseBinDate(data.by_error),
  error_rate: parseBinDate(data.error_rate),
});

export const useErrors = (
  baseUrl: Nullable<string> = null,
  { versions, errorCodes, timeWindow, flowId }: ErrorsFilters,
) => {
  const { startDate, endDate } = getISODatesFromTimeWindow(timeWindow);
  const interval = getIntervalForTimeWindow(timeWindow);
  const isIcebergMetricsEnabled = useIsIcebergMetricsEnabled();

  const enabled =
    Boolean(baseUrl) && (versions === undefined || versions.length > 0);

  return useQuery({
    queryKey: [
      "metrics",
      "errors",
      startDate,
      endDate,
      flowId,
      isIcebergMetricsEnabled,
      ...(versions ?? ["all_versions"]),
      ...errorCodes,
    ],
    queryFn: () =>
      ExporterMetricsEndpoint.getErrors(
        baseUrl!,
        {
          versions,
          startDate,
          endDate,
          interval: intervalToSeconds(interval),
          errorCodes,
          flowId,
        },
        isIcebergMetricsEnabled,
      ),
    enabled,
    staleTime: ONE_MINUTE_IN_MS,
    select: parseErrors,
    // Disable to prevent race condition when rapidly switching version filters
    experimental_prefetchInRender: false,
  });
};

const parseLatency = (data: MetricsLatencyResponse) => ({
  by_version: parseBinDate(data.by_version),
  total: parseBinDate(data.total),
});

type LatencyFilters = PerformanceFilters & {
  func: LatencyFunction;
};

export const useLatency = (
  baseUrl: Nullable<string> = null,
  { versions, timeWindow, func, flowId }: LatencyFilters,
) => {
  const { startDate, endDate } = getISODatesFromTimeWindow(timeWindow);
  const interval = getIntervalForTimeWindow(timeWindow);
  const isIcebergMetricsEnabled = useIsIcebergMetricsEnabled();

  const enabled =
    Boolean(baseUrl) && (versions === undefined || versions.length > 0);

  return useQuery({
    queryKey: [
      "metrics",
      "latency",
      startDate,
      endDate,
      func,
      flowId,
      isIcebergMetricsEnabled,
      ...(versions ?? ["all_versions"]),
    ],
    queryFn: () =>
      ExporterMetricsEndpoint.getLatency(
        baseUrl!,
        {
          versions,
          startDate,
          endDate,
          interval: intervalToSeconds(interval),
          func,
          flowId,
        },
        isIcebergMetricsEnabled,
      ),
    enabled,
    staleTime: ONE_MINUTE_IN_MS,
    select: parseLatency,
    // Disable to prevent race condition when rapidly switching version filters
    experimental_prefetchInRender: false,
  });
};

type UseNodeStatusCountsParams = {
  baseUrl: string;
  jobRunId: string;
  flowId: string;
  environment: string;
  fromStartTime: string;
  untilStartTime: string;
  isRunning?: boolean;
  expectedTotal: number;
  terminatedAt?: string;
  outputNodeId: string;
};

const POLL_INTERVAL_MS = 5000;

export const MAX_HISTORY_WAIT_MS = 2 * 60 * 1000;

export const useNodeStatusCounts = ({
  baseUrl,
  jobRunId,
  flowId,
  environment,
  fromStartTime,
  untilStartTime,
  isRunning = false,
  expectedTotal,
  terminatedAt,
  outputNodeId,
}: UseNodeStatusCountsParams) => {
  return useQuery<MetricsNodeStatusCountsResponse>({
    queryKey: ["metrics", "node_status_counts", jobRunId],
    queryFn: () =>
      ExporterMetricsEndpoint.getNodeStatusCounts(
        baseUrl,
        jobRunId,
        flowId,
        environment,
        fromStartTime,
        untilStartTime,
      ),
    enabled: Boolean(jobRunId),
    refetchInterval: (query) => {
      if (expectedTotal === 0) return false;
      if (isRunning) return POLL_INTERVAL_MS;

      const data = query.state.data;
      if (!data) return POLL_INTERVAL_MS;

      if (terminatedAt) {
        const msSinceTerminated = Date.now() - new Date(terminatedAt).getTime();
        if (msSinceTerminated > MAX_HISTORY_WAIT_MS) return false;
        return POLL_INTERVAL_MS;
      }

      const runningTotal = computeRunningTotal(data, outputNodeId);
      return runningTotal < expectedTotal ? POLL_INTERVAL_MS : false;
    },
  });
};
